/**
 * Analytics Event Constants
 *
 * Centralized event names for PostHog analytics tracking.
 * Organized by feature area for maintainability.
 *
 * Naming Convention:
 * - Use snake_case for event names
 * - Use past tense for completed actions (e.g., 'restaurant_rated')
 * - Use present tense for clicks/interactions (e.g., 'login_clicked')
 */

export const AnalyticsEvents = {
  // ============================================
  // AUTHENTICATION & ONBOARDING
  // ============================================
  LOGIN_CLICKED: 'login_clicked',
  LOGIN_COMPLETED: 'login_completed',
  FORGOT_PASSWORD_CLICKED: 'forgot_password_clicked',
  SIGNUP_CLICKED: 'signup_clicked',
  SIGNUP_COMPLETED: 'signup_completed',
  SIGNUP_ABANDONED: 'signup_abandoned',
  LOGOUT: 'logout',
  ONBOARDING_COMPLETED: 'onboarding_completed',

  // ============================================
  // ONBOARDING GATE (D43a — FR-A21, NFR-7)
  // ============================================
  GATE_STEP_STARTED: 'onboarding_gate_step_started',
  GATE_STEP_COMPLETED: 'onboarding_gate_step_completed',
  GATE_STEP_ABANDONED: 'onboarding_gate_step_abandoned',
  GATE_RELEASED: 'onboarding_gate_released',

  // ============================================
  // QUIZ-3 PROMPT (D43a — FR-A13-A16)
  // ============================================
  QUIZ3_PROMPT_SHOWN: 'quiz3_prompt_shown',
  QUIZ3_PROMPT_ACCEPTED: 'quiz3_prompt_accepted',
  QUIZ3_PROMPT_DECLINED: 'quiz3_prompt_declined',
  QUIZ3_PROMPT_RETIRED: 'quiz3_prompt_retired',

  // ============================================
  // QUIZ NUDGE (D43a — FR-A17, FR-A18)
  // ============================================
  QUIZ_NUDGE_SHOWN: 'quiz_nudge_shown',
  QUIZ_NUDGE_ACCEPTED: 'quiz_nudge_accepted',
  QUIZ_NUDGE_DECLINED: 'quiz_nudge_declined',
  QUIZ_NUDGE_RETIRED: 'quiz_nudge_retired',

  // ============================================
  // QUIZ SYSTEM
  // ============================================
  QUIZ_STARTED: 'quiz_started',
  QUIZ_QUESTION_ANSWERED: 'quiz_question_answered',
  QUIZ_ANSWER_CHANGED: 'quiz_answer_changed',
  QUIZ_COMPLETED: 'quiz_completed',
  QUIZ_ABANDONED: 'quiz_abandoned',
  QUIZ_SWITCHED: 'quiz_switched',
  QUIZ_PHOTO_CLICKED: 'quiz_photo_clicked',
  QUIZ_LEARN_MORE_CLICKED: 'quiz_learn_more_clicked',
  QUIZ_WHAT_IS_THIS_CLICKED: 'quiz_what_is_this_clicked',

  // ============================================
  // RESTAURANT INTERACTIONS
  // ============================================
  RESTAURANT_VIEWED: 'restaurant_viewed',
  OUTLET_POST_VIEWED: 'outlet_post_viewed',
  RESTAURANT_PIN_CLICKED: 'restaurant_pin_clicked',
  RESTAURANT_CARD_CLICKED: 'restaurant_card_clicked',
  RESTAURANT_RATED: 'restaurant_rated',
  RESTAURANT_RATING_CHANGED: 'restaurant_rating_changed',
  RESTAURANT_CHECK_IN_CLICKED: 'restaurant_check_in_clicked',
  RESTAURANT_NOTES_CLICKED: 'restaurant_notes_clicked',
  RESTAURANT_NOTES_SAVED: 'restaurant_notes_saved',
  RESTAURANT_SHARED: 'restaurant_shared',
  RESTAURANT_WEBSITE_CLICKED: 'restaurant_website_clicked',
  RESTAURANT_ORDERS_CLICKED: 'restaurant_orders_clicked',
  RESTAURANT_PHONE_CLICKED: 'restaurant_phone_clicked',
  RESTAURANT_CUSTOM_LINK_CLICKED: 'restaurant_custom_link_clicked',
  RESTAURANT_MENU_CLICKED: 'restaurant_menu_clicked',
  RESTAURANT_DIRECTIONS_CLICKED: 'restaurant_directions_clicked',
  MENU_ITEM_RATE_CLICKED: 'menu_item_rate_clicked',
  MENU_ITEM_EDIT_CLICKED: 'menu_item_edit_clicked',

  // ============================================
  // SEARCH & DISCOVERY
  // ============================================
  SEARCH_RESULT_IMPRESSION: 'search_result_impression',
  SEARCH_PERFORMED: 'search_performed',
  SEARCH_LOCATION_ENTERED: 'search_location_entered',
  SEARCH_FILTER_CLICKED: 'search_filter_clicked',
  SEARCH_FILTER_NEARBY_CLICKED: 'search_filter_nearby_clicked',
  SEARCH_FILTER_COMPATIBILITY_CLICKED: 'search_filter_compatibility_clicked',
  MAP_VIEW_CLICKED: 'map_view_clicked',
  // Member-initiated pan/zoom on the search map (RestaurantMapView). Programmatic
  // camera moves (card recenter, city-search flyTo) do not fire it.
  MAP_INTERACTED: 'map_interacted',
  MAP_LIST_CLICKED: 'map_list_clicked',
  TASTE_MATCH_CLICKED: 'taste_match_clicked',
  PUBLIC_OPINION_CLICKED: 'public_opinion_clicked',
  SURPRISE_ME_CLICKED: 'surprise_me_clicked',

  // D61 Phase 5 — unified search dropdown
  SEARCH_SUGGESTION_SELECTED: 'search_suggestion_selected',
  SEARCH_RECENT_SELECTED: 'search_recent_selected',
  /** Carries the selected row's position within the open dropdown. */
  SEARCH_NEARBY_CLICKED: 'search_nearby_clicked',

  // ============================================
  // LISTS
  // ============================================
  LIST_CREATED: 'list_created',
  LIST_CLICKED: 'list_clicked',
  LIST_DELETED: 'list_deleted',
  LIST_NAME_EDIT_CLICKED: 'list_name_edit_clicked',
  LIST_SEARCH_PERFORMED: 'list_search_performed',
  LAST_VIEWED_LIST_CLICKED: 'last_viewed_list_clicked',
  NEW_LIST_CLICKED: 'new_list_clicked',
  LIST_COPIED: 'list_copied',
  LIST_SHARED: 'list_shared',
  LIST_FOLLOWED: 'list_followed',
  LIST_UNFOLLOWED: 'list_unfollowed',
  RESTAURANT_ADDED_TO_LIST: 'restaurant_added_to_list',
  RESTAURANT_REMOVED_FROM_LIST: 'restaurant_removed_from_list',
  RESTAURANT_POSITION_CHANGED: 'restaurant_position_changed',
  LIST_TAG_ADDED: 'list_tag_added',
  LIST_TAG_REMOVED: 'list_tag_removed',

  // ============================================
  // COLLABORATIVE LISTS & VOTING
  // ============================================
  COLLAB_LIST_CREATED: 'collab_list_created',
  COLLAB_REGULAR_VOTE_STARTED: 'collab_regular_vote_started',
  COLLAB_SURVIVOR_VOTE_STARTED: 'collab_survivor_vote_started',
  COLLAB_RANKING_STARTED: 'collab_ranking_started',
  COLLAB_VOTE_CAST: 'collab_vote_cast',
  COLLAB_MEMBERS_INVITED: 'collab_members_invited',
  COLLAB_INVITATION_ACCEPTED: 'collab_invitation_accepted',
  COLLAB_INVITATION_DECLINED: 'collab_invitation_declined',
  COLLAB_VOTING_ENDED: 'collab_voting_ended',
  COLLAB_LIST_DELETED: 'collab_list_deleted',
  COLLAB_LIST_LEFT: 'collab_list_left',

  // ============================================
  // SOCIAL & FEED
  // ============================================
  FEED_MEMBER_TAG_CLICKED: 'feed_member_tag_clicked',
  FEED_RESTAURANT_TAG_CLICKED: 'feed_restaurant_tag_clicked',
  MEMBER_PROFILE_CLICKED: 'member_profile_clicked',
  MEMBER_PROFILE_BUTTON_CLICKED: 'member_profile_button_clicked',
  POST_CREATED: 'post_created',
  POST_LIKED: 'post_liked',
  POST_SHARED: 'post_shared',
  POST_REACTED: 'post_reacted',
  COMMENT_CREATED: 'comment_created',
  STORY_CREATED: 'story_created',
  STORY_VIEWED: 'story_viewed',
  FRIEND_REQUEST_SENT: 'friend_request_sent',
  FRIEND_REQUEST_ACCEPTED: 'friend_request_accepted',
  FRIEND_REQUEST_REJECTED: 'friend_request_rejected',
  USER_FOLLOWED: 'user_followed',
  USER_UNFOLLOWED: 'user_unfollowed',
  USER_BLOCKED: 'user_blocked',
  POLL_VOTED: 'poll_voted',
  CHECK_IN_CREATED: 'check_in_created',

  // ============================================
  // PROFILE & SETTINGS
  // ============================================
  PROFILE_PHOTO_UPDATED: 'profile_photo_updated',
  HOME_LOCATION_CHANGED: 'home_location_changed',
  TASTE_INSIGHTS_VIEWED: 'taste_insights_viewed',
  FRIEND_INSIGHTS_VIEWED: 'friend_insights_viewed',
  TASTE_PROFILE_SHARED: 'taste_profile_shared',
  TASTE_PROFILE_SHARE_THIRD_PARTY_CLICKED: 'taste_profile_share_third_party_clicked',
  NOTIFICATION_PREFERENCES_UPDATED: 'notification_preferences_updated',
  CUISINE_CHIP_LEARN_MORE_CLICKED: 'cuisine_chip_learn_more_clicked',

  // ============================================
  // NAVIGATION & UI
  // ============================================
  PAGE_VIEW: '$pageview', // PostHog standard event
  BOTTOM_NAV_CLICKED: 'bottom_nav_clicked',
  BURGER_MENU_CLICKED: 'burger_menu_clicked',
  HISTORY_CLICKED: 'history_clicked',
  RATE_BUTTON_CLICKED: 'rate_button_clicked',
  LIST_BUTTON_CLICKED: 'list_button_clicked',
  FAQ_CLICKED: 'faq_clicked',
  SPECIFIC_FAQ_CLICKED: 'specific_faq_clicked',
  FEEDBACK_CLICKED: 'feedback_clicked',
  NOTIFICATION_CLICKED: 'notification_clicked',
  TAB_SWITCHED: 'tab_switched',

  // ============================================
  // SESSION TRACKING
  // ============================================
  APP_OPENED: 'app_opened',
  APP_FOREGROUNDED: 'app_foregrounded',
  APP_BACKGROUNDED: 'app_backgrounded',
} as const;

/**
 * Type for event names
 */
export type AnalyticsEventName = (typeof AnalyticsEvents)[keyof typeof AnalyticsEvents];
